import { toast } from "react-toastify";

import {
  UpsellProductRulesDto,
  UpsellProductRulesNodeType,
} from "@intelligems/ig-types/dtos/upsell-product-rules";
import { UpsellProductRulesActionExpressionNodeQueryFe } from "@intelligems/ig-types/fe-only/upsell-product-rules";
import { SetUpsellProductRulesOutput } from "@intelligems/ig-types/schemas/ai/ui-actions/set-upsell-product-rules";
import { fromGqlId, toGqlId } from "@src/api/formatters/shopify";
import { createAsyncThunk } from "@src/store/createAsyncThunk";
import { PRODUCT_PICKER_VARIANTS_PAGE_SIZE } from "@src/store/slices/productPicker";
import { ProductPickerProduct } from "@src/store/slices/productPicker/types";
import { loadProductsWithVariants } from "@src/store/slices/productPicker/util/loadProductsWithVariants";
import { selectionToUpsellProductItems } from "@src/store/slices/productPicker/util/upsellSelection";
import { replaceUpsellProductRules } from "@src/store/slices/upsellProductRules/thunks/replaceUpsellProductRules";

type ProductsToLoad = SetUpsellProductRulesOutput["productsToLoad"];

/**
 * Fills each Ellie "products" rule's empty `productId` item list with the
 * products she picked, loaded with the same Shopify query and turned into
 * items by the same `selectionToUpsellProductItems` the editor's product
 * picker uses — so an Ellie rule is identical to one the merchant built.
 * `loadProducts` takes bare numeric product ids.
 */
export const fillAiUpsellRuleProducts = async ({
  rules,
  productsToLoad,
  loadProducts,
}: {
  rules: UpsellProductRulesDto[];
  productsToLoad: ProductsToLoad;
  loadProducts: (productIds: string[]) => Promise<ProductPickerProduct[]>;
}): Promise<UpsellProductRulesDto[]> => {
  if (productsToLoad.length === 0) {
    return rules;
  }

  const productIds = [
    ...new Set(
      productsToLoad.flatMap(({ products }) =>
        products.map((product) => fromGqlId(product.productId))
      )
    ),
  ];
  const loadedProducts = await loadProducts(productIds);

  return rules.map((rule) => {
    const requested = productsToLoad.find(({ ruleId }) => ruleId === rule.id);
    if (!requested) {
      return rule;
    }

    const items = selectionToUpsellProductItems(
      requested.products.map((product) => ({
        id: fromGqlId(product.productId),
        title: "",
        // The picker's variant ids are gids; omitted means every variant.
        selectedVariantIds: product.variantIds?.map((variantId) =>
          toGqlId(variantId, "ProductVariant")
        ),
      })),
      loadedProducts
    );
    const displayData = Object.fromEntries(
      items.map((item) => [
        item.productId,
        { title: item.productTitle, image: item.productImage ?? undefined },
      ])
    );

    const expression = rule.action.expression.map(
      (node): UpsellProductRulesNodeType =>
        node.query?.type === "productId"
          ? {
              query: {
                ...node.query,
                value: items,
                displayData,
              } as UpsellProductRulesActionExpressionNodeQueryFe,
            }
          : node
    );

    return { ...rule, action: { ...rule.action, expression } };
  });
};

/**
 * Applies the rules Ellie's `setUpsellProductRules` tool built: loads the
 * explicit products she picked, then hands off to `replaceUpsellProductRules`
 * like the editor. Nothing is applied if the products can't be loaded.
 */
export const applyAiUpsellProductRules = createAsyncThunk<
  void,
  {
    checkoutBlockId: string;
    rules: UpsellProductRulesDto[];
    productsToLoad: ProductsToLoad;
  }
>(
  "upsellProductRules/applyAiUpsellProductRules",
  async ({ checkoutBlockId, rules, productsToLoad }, { dispatch }) => {
    try {
      const filledRules = await fillAiUpsellRuleProducts({
        rules,
        productsToLoad,
        loadProducts: async (productIds) =>
          (
            await loadProductsWithVariants({
              first: productIds.length,
              query: productIds.map((id) => `id:${id}`).join(" OR "),
              variantsFirst: PRODUCT_PICKER_VARIANTS_PAGE_SIZE,
            })
          ).data,
      });
      await dispatch(
        replaceUpsellProductRules({ checkoutBlockId, rules: filledRules })
      );
    } catch (error) {
      toast.error(
        "Couldn't load the upsell products Ellie picked. Try again, or add them in the upsell settings."
      );
      throw error;
    }
  }
);
